import { AlertEnum, ButtonVariantEnum, TipoGrupoEleitoresEnum } from '../../enums';
import { Button, IconButton } from '../../components';
import { FaExclamationTriangle, FaPen, FaSortNumericDown, FaTrashAlt, FaWeightHanging } from 'react-icons/fa';
import { FunctionComponent, useEffect } from 'react';
import { GrupoEleitoresResponseType, GruposEleitoresCardListType } from '../../types';
import { useAlert, useAuthAxios, useErrorHandler, useModal } from '../../hooks';

import { ApiHelper } from '../../helpers';
import GruposEleitoresCardItem from './GruposEleitoresCardItem';
import { LabelValue } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const GruposEleitoresCardList: FunctionComponent<GruposEleitoresCardListType> = ({
  idEleicao,
  gruposEleitores,
  setGruposEleitores,
  permiteAlterarDadosEleicao,
}) => {
  const { t } = useTranslation();
  const { confirmModal } = useModal();
  const { createAlert } = useAlert();
  const axios = useAuthAxios();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();

  useEffect(() => {
    if (gruposEleitores.some((g: GrupoEleitoresResponseType) => g.totais?.length! > 1)) {
      createAlert(t('texto.aviso_eleicao_com_grupo_multiplos_tipos'), AlertEnum.WARNING);
    }
  }, [gruposEleitores]);

  return gruposEleitores.length > 0 ? (
    gruposEleitores.map((grupoEleitores: GrupoEleitoresResponseType) => {
      return (
        <div className="card mb-3 m-0 mb-0-last nested bg-primary-050 border " key={grupoEleitores.id}>
          <div className="card-header bg-white">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
              <div className="d-flex fw-bold align-items-center">
                {grupoEleitores.denominacao}
                {grupoEleitores.totais?.length! > 1 && (
                  <FaExclamationTriangle className="text-warning ms-1" title={t('label.grupo_com_mais_de_um_tipo_eleitor')} />
                )}
              </div>
              <div className="d-flex align-items-center gap-3">
                <LabelValue
                  label={t('label.prioridade')}
                  value={grupoEleitores.prioridade}
                  icon={FaSortNumericDown}
                  iconSize={'1em'}
                  boldValue
                />

                <LabelValue
                  label={t('label.peso')}
                  value={grupoEleitores?.peso!}
                  icon={FaWeightHanging}
                  iconSize={'1em'}
                  boldValue
                />

                <div className="d-flex gap-2">
                  <IconButton
                    Icon={FaPen}
                    variant={ButtonVariantEnum.PRIMARY}
                    onClick={(event) => {
                      navigate(`/eleicoes/${idEleicao}/grupos-eleitores/${grupoEleitores.id}/alterar`);
                      event.preventDefault();
                    }}
                    disabled={!permiteAlterarDadosEleicao}
                    title={t('label.alterar')}
                  />
                  <IconButton
                    Icon={FaTrashAlt}
                    variant={ButtonVariantEnum.DANGER}
                    onClick={(event) => {
                      confirmModal(
                        t('confirmacao.remover_grupo_eleitores'),
                        () => {
                          axios
                            .delete(ApiHelper.getUrl.apiSCE.grupoEleitores(+idEleicao!, grupoEleitores.id))
                            .then(() => {
                              createAlert(t('sucesso.remover_grupo_eleitores'), AlertEnum.SUCCESS);
                              setGruposEleitores(
                                gruposEleitores.filter((g: GrupoEleitoresResponseType) => g.id !== grupoEleitores.id),
                              );
                            })
                            .catch((error) => errorHandler(error, t('erro.remover_grupo_eleitores')));
                        },
                        ButtonVariantEnum.DANGER,
                        t('label.remover_grupo_eleitores'),
                      );
                      event.preventDefault();
                    }}
                    disabled={!permiteAlterarDadosEleicao}
                    title={t('label.remover')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-2">
              <div className="col">
                <GruposEleitoresCardItem
                  tipoDescricao={t('label.docentes')}
                  icon={TipoGrupoEleitoresEnum.findByKey('DOCENTE').icon}
                  totalEleitoresUnicos={
                    (grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DOCENTE.key)?.unicos ?? 0) +
                    (grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DOCENTE_EXTERNO.key)
                      ?.unicos ?? 0)
                  }
                  totalEleitores={
                    (grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DOCENTE.key)?.total ?? 0) +
                    (grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DOCENTE_EXTERNO.key)
                      ?.total ?? 0)
                  }
                  linkGerenciamentoEleitores={`/eleicoes/${idEleicao}/grupos-eleitores/alterar/${grupoEleitores!.id}/docentes`}
                  permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
                />
              </div>
              <div className="col">
                <GruposEleitoresCardItem
                  tipoDescricao={t('label.tecnicos_administrativos')}
                  icon={TipoGrupoEleitoresEnum.findByKey('TECNICO_ADMINISTRATIVO').icon}
                  totalEleitoresUnicos={
                    grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.TECNICO_ADMINISTRATIVO.key)
                      ?.unicos
                  }
                  totalEleitores={
                    grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.TECNICO_ADMINISTRATIVO.key)
                      ?.total
                  }
                  linkGerenciamentoEleitores={`/eleicoes/${idEleicao}/grupos-eleitores/alterar/${grupoEleitores!.id}/tecnicos`}
                  permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
                />
              </div>
              <div className="col">
                <GruposEleitoresCardItem
                  tipoDescricao={t('label.discentes')}
                  icon={TipoGrupoEleitoresEnum.findByKey('DISCENTE').icon}
                  totalEleitoresUnicos={
                    grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DISCENTE.key)?.unicos
                  }
                  totalEleitores={
                    grupoEleitores!.totais?.find((total) => total.tipo === TipoGrupoEleitoresEnum.DISCENTE.key)?.total
                  }
                  linkGerenciamentoEleitores={`/eleicoes/${idEleicao}/grupos-eleitores/alterar/${grupoEleitores!.id}/discentes`}
                  permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
                />
              </div>
            </div>
          </div>

          <div className="card-footer bg-white text-center">
            <LabelValue label={t('label.total_eleitores_validos')} value={grupoEleitores.eleitores_unicos} boldValue />
          </div>
        </div>
      );
    })
  ) : (
    <div className="d-flex flex-column align-items-center justify-content-center ">
      <p>{t('texto.nenhum_grupo_eleitores')}</p>
      {permiteAlterarDadosEleicao && (
        <Button title={t('label.novo_grupo')} onClick={() => navigate(`/eleicoes/${idEleicao}/grupos-eleitores/cadastrar`)} />
      )}
    </div>
  );
};

export default GruposEleitoresCardList;
